import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  LanguageIcon,
  PhotoIcon,
} from "@heroicons/react/20/solid";
import { BookmarkIcon } from "@heroicons/react/24/solid";
import { BookmarkIcon as BookmarkIconOutline } from "@heroicons/react/24/outline";

import type { AppItem } from "@/types/appItem";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardDescription,
} from "@/ui/Card";
import Modal from "@/ui/Modal";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/shad_cn";
import { trackEvent } from "@/services/analyticsService";

interface AppCardProps {
  app: AppItem;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

// Dedicated icons for specific apps
const APP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "elanco-ai-chat": ChatBubbleLeftRightIcon,
  "elanco-ai-translate": LanguageIcon,
  "ai-media-generation": PhotoIcon,
};

interface AppIconProps {
  app: AppItem;
  size?: "sm" | "md";
}

const AppIcon = ({ app, size = "md" }: AppIconProps) => {
  const Icon = APP_ICONS[app.id] || ComputerDesktopIcon;
  const sizeClasses = size === "sm" ? "h-10 w-10" : "h-12 w-12";
  const iconSize = size === "sm" ? "h-5 w-5" : "h-6 w-6";

  if (app.logo) {
    return (
      <div className={cn("relative shrink-0 overflow-hidden", sizeClasses)}>
        <img
          src={app.logo}
          alt={`${app.name} logo`}
          className="h-full w-full object-contain"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0072ce] to-[#005ba1] shadow-sm",
        sizeClasses,
      )}
    >
      <Icon className={cn(iconSize, "text-white")} />
    </div>
  );
};

interface IconButtonProps {
  onClick: () => void;
  label: string;
  isActive?: boolean;
  children: React.ReactNode;
  className?: string;
}

const IconButton = ({
  onClick,
  label,
  isActive,
  children,
  className,
}: IconButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-lg p-1.5 transition-all duration-200",
      isActive ? "text-[#0072ce]" : "text-gray-400 hover:text-gray-600",
      className,
    )}
    aria-label={label}
  >
    {children}
  </button>
);

interface LaunchButtonProps {
  url: string;
  isExternal?: boolean | undefined;
  onLaunch?: () => void;
}

const LaunchButton = ({ url, isExternal, onLaunch }: LaunchButtonProps) => {
  const buttonContent = (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#0072ce] px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-[#005ba1] hover:shadow-md">
      {isExternal ? "Open" : "Launch"}
      {isExternal && <ArrowTopRightOnSquareIcon className="h-4 w-4" />}
    </span>
  );

  if (isExternal) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLaunch}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <Link to={url} onClick={onLaunch}>
      {buttonContent}
    </Link>
  );
};

const AppCard = ({ app, isFavorite, onToggleFavorite }: AppCardProps) => {
  const { isMobile } = useTheme();
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const hasUrl = Boolean(app.url);

  const handleLaunch = () => {
    trackEvent("app_launch", {
      app_id: app.id,
      app_name: app.name,
      is_external: Boolean(app.isExternal),
    });
  };

  // Action icons for header row
  const actionIcons = (
    <div className="flex items-center gap-1">
      {/* Info icon - mobile only */}
      {isMobile && (
        <IconButton
          onClick={() => setIsDescriptionModalOpen(true)}
          label="View description"
        >
          <InformationCircleIcon className="h-5 w-5" />
        </IconButton>
      )}

      {/* Docs icon */}
      {app.docsUrl && (
        <a
          href={app.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg p-1.5 text-gray-400 transition-all duration-200 hover:text-gray-600"
          aria-label="View documentation"
          onClick={() =>
            trackEvent("app_docs_click", { app_id: app.id, app_name: app.name })
          }
        >
          <DocumentTextIcon className="h-5 w-5" />
        </a>
      )}

      {/* Bookmark/Favorite icon */}
      <IconButton
        onClick={() => {
          trackEvent("app_favorite", {
            app_id: app.id,
            app_name: app.name,
            delta: isFavorite ? -1 : 1,
          });
          onToggleFavorite(app.id);
        }}
        label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        isActive={isFavorite}
      >
        {isFavorite ? (
          <BookmarkIcon className="h-5 w-5" />
        ) : (
          <BookmarkIconOutline className="h-5 w-5" />
        )}
      </IconButton>
    </div>
  );

  // Mobile layout: Compact horizontal card
  if (isMobile) {
    return (
      <>
        <Card className={cn(!hasUrl && "opacity-75")}>
          {/* Header row: Icon + Title + Actions */}
          <CardHeader className="flex-row items-center gap-3">
            <AppIcon app={app} size="sm" />
            <h3 className="flex-1 text-sm leading-tight font-semibold text-gray-900">
              {app.name}
            </h3>
            {actionIcons}
          </CardHeader>

          <CardFooter className="justify-between pt-3">
            {app.category ? (
              <span className="inline-block rounded-full bg-[#0072ce]/10 px-2 py-0.5 text-xs font-medium text-[#0072ce]">
                {app.category}
              </span>
            ) : (
              <div />
            )}

            {hasUrl && app.url ? (
              <LaunchButton
                url={app.url}
                isExternal={app.isExternal}
                onLaunch={handleLaunch}
              />
            ) : (
              <span className="text-xs text-gray-500">Coming soon...</span>
            )}
          </CardFooter>
        </Card>

        <Modal
          isOpen={isDescriptionModalOpen}
          onClose={() => setIsDescriptionModalOpen(false)}
          title={app.name}
        >
          <p className="leading-relaxed">{app.description}</p>
        </Modal>
      </>
    );
  }

  // Desktop layout: Header row with icon + title + actions, then content
  return (
    <Card className={cn("h-full", !hasUrl && "opacity-75")}>
      {/* Header row: Icon + Title + Actions (like Figma design) */}
      <CardHeader className="flex-row items-center gap-3">
        <AppIcon app={app} size="md" />
        <h3 className="flex-1 text-lg font-semibold text-gray-900">
          {app.name}
        </h3>
        {actionIcons}
      </CardHeader>

      <CardContent className="mt-3">
        <CardDescription className="line-clamp-3" title={app.description}>
          {app.description}
        </CardDescription>

        {app.category && (
          <div className="mt-3">
            <span className="inline-block rounded-full bg-[#0072ce]/10 px-3 py-1 text-xs font-medium text-[#0072ce]">
              {app.category}
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter className="mt-auto justify-start">
        {hasUrl && app.url ? (
          <LaunchButton url={app.url} isExternal={app.isExternal} />
        ) : (
          <span className="text-sm text-gray-500">Coming soon...</span>
        )}
      </CardFooter>
    </Card>
  );
};

export default AppCard;
